import React, { useEffect, useRef, useState } from "react";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { fetchBootstrapStatus } from "../lib/api";

const POLL_MS = 2500;
const SETTLE_HIDE_MS = 4500;

/**
 * Live status pill shown only during a first-run DCC bootstrap.
 *
 * Renders nothing in the steady state (DB already populated). When the
 * backend reports `needed && running`, polls every 2.5s and shows a live
 * "X imported" count. Briefly shows a success state before hiding itself
 * once the import settles.
 */
export function BootstrapPill() {
  const [state, setState] = useState(null); // null = not loaded yet
  const [hidden, setHidden] = useState(false);
  const hideTimer = useRef(null);
  const intervalRef = useRef(null);

  const fetchOnce = async () => {
    try {
      const s = await fetchBootstrapStatus();
      setState(s);
    } catch {
      // Backend hiccup — keep last known state.
    }
  };

  useEffect(() => {
    let cancelled = false;
    fetchOnce();

    intervalRef.current = setInterval(async () => {
      if (cancelled) return;
      await fetchOnce();
    }, POLL_MS);

    return () => {
      cancelled = true;
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, []);

  // Once the import finishes, schedule a fade-out + stop polling.
  useEffect(() => {
    if (!state) return;
    if (state.needed && !state.running && !hideTimer.current) {
      // success toast just once
      if (state.error) {
        toast.error("DCC bootstrap failed", { description: state.error });
      } else if (state.imported > 0) {
        toast.success(
          `Imported ${state.imported.toLocaleString()} dispensaries from CA DCC`,
          { description: "You can now filter, map, or generate a lead list." }
        );
      }
      hideTimer.current = setTimeout(() => {
        setHidden(true);
        if (intervalRef.current) clearInterval(intervalRef.current);
      }, SETTLE_HIDE_MS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.needed, state?.running]);

  if (!state || hidden) return null;
  if (!state.needed) return null; // steady state — never show
  // Hide on subsequent page loads long after the bootstrap settled.
  if (state.finished_at) {
    const ageMs = Date.now() - new Date(state.finished_at).getTime();
    if (ageMs > 30_000) return null;
  }

  const running = state.running;
  const errored = !running && !!state.error;
  const finished = !running && !errored;

  return (
    <div
      data-testid="bootstrap-pill"
      className={`hidden md:inline-flex items-center gap-2 h-9 px-3 rounded-full border text-xs font-mono transition-colors ${
        errored
          ? "border-rose-500/40 bg-rose-500/10 text-rose-200"
          : finished
          ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
          : "border-emerald-500/30 bg-emerald-500/5 text-emerald-200"
      }`}
    >
      {running ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-400" />
      ) : errored ? (
        <AlertTriangle className="w-3.5 h-3.5 text-rose-300" />
      ) : (
        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300" />
      )}
      <span className="leading-none whitespace-nowrap">
        {running && (
          <>
            Loading CA DCC license list…{" "}
            <span data-testid="bootstrap-pill-count" className="text-emerald-100 font-semibold">
              {(state.current_count || 0).toLocaleString()}
            </span>{" "}
            imported
          </>
        )}
        {errored && <>DCC bootstrap failed — check logs</>}
        {finished && (
          <>
            DCC bootstrap complete ·{" "}
            <span className="text-emerald-100 font-semibold">
              {state.imported.toLocaleString()}
            </span>{" "}
            dispensaries
          </>
        )}
      </span>
    </div>
  );
}

export default BootstrapPill;
